import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import { DateYMD } from '../lib/dates';

import { Course, CourseRun, CourseRunCustomer, CustomerCourse } from '../models/course';
import { Reservation } from '../models/reservation';

export class CourseService extends BaseService<Course> {
  override model = Course;
  override modelName = 'course';
  override modelLabel = 'Course';

  async listRuns(
    course: Course,
    filters: Record<string, string | number> = {},
  ): Promise<ApiListResponse<CourseRun>> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      query[key] = String(value);
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${course.objectID()}/runs`,
      query,
    });

    return await this.requestList(request, CourseRun);
  }

  async getRun(
    course: Course,
    idCourseRun: number,
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${course.objectID()}/runs/${idCourseRun}`,
    });

    return await this.requestItem(request, CourseRun);
  }

  async createRun(
    course: Course,
    data: Record<string, any>,
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${course.objectID()}/runs`,
      data,
    });

    return await this.requestItem(request, CourseRun);
  }

  async updateRun(
    course: Course,
    data: Record<string, any> & { idCourseRun: number },
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${course.objectID()}/runs/${data.idCourseRun}`,
      data,
    });

    return await this.requestItem(request, CourseRun);
  }

  async deleteRun(
    course: Course,
    idCourseRun: number,
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${course.objectID()}/runs/${idCourseRun}`,
    });

    return await this.requestItem(request, CourseRun);
  }

  async addCustomer(
    course: Course,
    {
      courseRun,
      idCustomer,
      confirm = true,
    }: {
      courseRun: CourseRun;
      idCustomer: number;
      confirm?: boolean;
    },
  ): Promise<ApiResponse<CustomerCourse>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${course.objectID()}/addCustomer/${courseRun.idCourseRun}`,
      data: {
        idCustomer,
        confirm: confirm ? 1 : 0,
      },
    });

    return await this.requestItem(request, CustomerCourse);
  }

  async upcomingRuns(
    { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
  ): Promise<ApiListResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl.replace('/course', '/courseRun')}/upcoming`,
      query: {
        page: page.toString(),
        limit: limit.toString(),
      },
    });

    return await this.requestList(request, CourseRun);
  }

  async addReservations(
    courseRun: CourseRun,
    reservations: Record<string, any>[],
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl.replace('/course', '/courseRun')}/${courseRun.objectID()}/addReservations`,
      data: { reservations },
    });

    return await this.requestItem(request, CourseRun);
  }

  async removeReservations(
    courseRun: CourseRun,
    reservations: Record<string, any>[],
    action = 'none',
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl.replace('/course', '/courseRun')}/${courseRun.objectID()}/removeReservations`,
      data: { reservations, action },
    });

    return await this.requestItem(request, CourseRun);
  }

  async getRunCustomers(
    courseRun: CourseRun,
  ): Promise<ApiListResponse<CourseRunCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl.replace('/course', '/courseRun')}/${courseRun.objectID()}/customers`,
    });

    return await this.requestList(request, CourseRunCustomer);
  }

  get courseRunBaseUrl(): string {
    return this.modelBaseUrl.replace('/course', '/courseRun');
  }

  async getRunDefaults(idCourse?: number | string): Promise<ApiResponse<CourseRun>> {
    const query = idCourse ? `?course=${idCourse}` : '';
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.courseRunBaseUrl}/new${query}`,
    });

    return await this.requestItem(request, CourseRun);
  }

  async getRunById(
    idCourseRun: number | string,
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.courseRunBaseUrl}/${idCourseRun}`,
    });

    return await this.requestItem(request, CourseRun);
  }

  async saveRun(
    data: Record<string, any>,
  ): Promise<ApiResponse<CourseRun>> {
    const isNew = !data.idCourseRun;
    const request = new ApiRequest({
      method: isNew ? 'POST' : 'PUT',
      url: isNew
        ? this.courseRunBaseUrl
        : `${this.courseRunBaseUrl}/${data.idCourseRun}`,
      data,
    });

    return await this.requestItem(request, CourseRun);
  }

  async deleteRunById(
    idCourseRun: number | string,
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.courseRunBaseUrl}/${idCourseRun}`,
    });

    return await this.requestItem(request, CourseRun);
  }

  async listAllRuns(): Promise<ApiListResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'GET',
      url: this.courseRunBaseUrl,
    });

    return await this.requestList(request, CourseRun);
  }

  async listCourseRuns({
    page = 1,
    limit = 100,
    filters = {},
  }: {
    page?: number;
    limit?: number;
    filters?: Record<string, string | number>;
  } = {}): Promise<ApiListResponse<CourseRun>> {
    const query: Record<string, string> = {
      page: String(page),
      limit: String(limit),
    };

    for (const [key, value] of Object.entries(filters)) {
      query[key] = String(value);
    }

    const request = new ApiRequest({
      method: 'GET',
      url: this.courseRunBaseUrl,
      query,
    });

    return await this.requestList(request, CourseRun);
  }

  async cancelRun(
    courseRun: CourseRun,
    {
      cancelReservations = false,
      sendEmail = false,
    }: {
      cancelReservations?: boolean;
      sendEmail?: boolean;
    } = {},
  ): Promise<ApiResponse<CourseRun>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.courseRunBaseUrl}/${courseRun.objectID()}/cancel`,
      data: {
        cancelReservations,
        sendEmail,
      },
    });

    return await this.requestItem(request, CourseRun);
  }

  /**
   * Duplicate a course run onto a new start date. Every class (reservation) is
   * recreated keeping the same number of days from the source run's first class,
   * so the weekly/spacing pattern and time of day are preserved. No enrolments
   * are copied and the new run is created as `confirmed`.
   *
   * `startDate` is the new date for the first class in `Ymd` format (e.g.
   * `20300203`). Accepts a raw `Ymd` string/number or a {@link DateYMD}.
   *
   * `information` is the display name for the new run and is required.
   */
  async duplicateRun(
    courseRun: CourseRun,
    startDate: string | number | DateYMD,
    information: string,
  ): Promise<ApiResponse<CourseRun>> {
    const ymd = startDate instanceof DateYMD ? startDate.integer : startDate;

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.courseRunBaseUrl}/${courseRun.objectID()}/duplicate`,
      data: {
        startDate: String(ymd),
        information,
      },
    });

    return await this.requestItem(request, CourseRun);
  }

  async availableReservations(
    courseRun: CourseRun,
    {
      serviceIDs,
      dates,
      employeeIDs,
    }: {
      serviceIDs?: Array<string | number>;
      dates?: string[];
      employeeIDs?: Array<string | number>;
    },
  ): Promise<ApiListResponse<Reservation>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.courseRunBaseUrl}/${courseRun.objectID()}/availableReservations`,
      data: {
        serviceIDs,
        dates,
        employeeIDs,
      },
    });

    return await this.requestList(request, Reservation);
  }

  async addPhoto(
    course: Course,
    image: File,
    onUploadProgress?: (progressEvent: any) => void,
  ): Promise<ApiResponse<Course>> {
    const formData = new FormData();
    formData.append('image', image);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${course.objectID()}/addPhoto`,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem(request, Course);
  }

  async deletePhoto(
    course: Course,
  ): Promise<ApiResponse<Course>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${course.objectID()}/deletePhoto`,
    });

    return await this.requestItem(request, Course);
  }
}
